import React, { useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Isvg from 'react-inlinesvg';
import { defineMessages } from 'react-intl';

import Button from 'components/Button';
import { Card } from 'components/Card';
import Message from 'components/Message';
import { openModal } from 'components/Modal';
import { TooltipToggle, TooltipContent } from 'components/Tooltip';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { isVenueDashboardSubtypeFoodAndBeverage } from 'helpers/ia-logic';
import { makePath } from 'helpers/link-generation';
import { Title } from 'features/App';
import { REPORT_TYPES } from 'features/Stats/Stats.constants';
import { selectActiveVenue, selectActiveVenueId, selectIsAppointmentTypeVenue } from 'selectors/general';

import { fitnessHero, fnbHero, wellnessHero } from './CampaignsV2.images';

import CampaignsTable from '../CampaignsTable';
import CampaignsContext, { CampaignsContextProvider } from './CampaignsContext';
import { HowItWorksCard1, HowItWorksCard2, HowItWorksCard3 } from './HowItWorks';
import './CampaignsV2.scss';

const messages = defineMessages({
  pageTitle: {
    id: 'campaigns.v2.page.title',
    defaultMessage: 'Campaigns',
  },
  activeCampaigns: {
    id: 'campaigns.v2.active-campaigns',
    defaultMessage: 'Active Campaigns',
  },
  viewReports: {
    id: 'campaigns.v2.view-reports',
    defaultMessage: 'View reports',
  },
  createCampaign: {
    id: 'campaigns.v2.create-campaign',
    defaultMessage: 'Create campaign',
  },
  madeFromPastCampaigns: {
    id: 'campaigns.v2.made-from-past-campaigns',
    defaultMessage: "You've made {revenue} from past campaigns.",
  },
  noActiveCampaigns: {
    id: 'campaigns.v2.no-active-campaigns',
    defaultMessage: "You don't have any active campaigns",
  },
  pastCampaigns: {
    id: 'campaigns.v2.past-campaigns',
    defaultMessage: 'Past Campaigns',
  },
  howItWorks: {
    id: 'campaigns.v2.how-it-works',
    defaultMessage: 'How it works',
  },
  heroHeading: {
    id: 'campaigns.v2.hero-heading',
    defaultMessage: 'ClassPass campaigns',
  },
  heroParagraph: {
    id: 'campaigns.v2.hero-paragraph',
    defaultMessage:
      'On average, campaigns increase impressions to your page by <strong>+16%</strong> and <strong>double reservations</strong>.',
  },
  learnMore: {
    id: 'campaigns.v2.learn-more',
    defaultMessage: 'Learn more',
  },
  createCampaignDisabledTooltip: {
    id: 'campaigns.v2.create-campaign-disable-tooltip',
    defaultMessage: 'All possible campaigns are currently active.',
  },
});

const { block, element } = bem('campaigns-v2');

function ActiveCampaigns() {
  const venueId = useSelector(selectActiveVenueId);
  const isAppointmentTypeVenue = useSelector(selectIsAppointmentTypeVenue);
  const {
    activeCampaigns: campaigns,
    isAnyCampaignOptionAvailable,
    totalRevenueFromCampaigns,
  } = useContext(CampaignsContext);
  const addUrl = makePath.campaignsAdd({ venueId });
  const reportsUrl = makePath.reports({ reportType: REPORT_TYPES.CAMPAIGNS });

  const { amount, currency } = totalRevenueFromCampaigns;

  return (
    <section className={element('section')}>
      <header className={element('header')}>
        <Message as="h3" className={element('heading')} message={messages.activeCampaigns} />

        <Link to={reportsUrl}>
          <Message as={Button} styled="link" message={messages.viewReports} />
        </Link>

        <When predicate={isAnyCampaignOptionAvailable}>
          <Link className={element('action', { createCampaign: true })} to={addUrl}>
            <Message as={Button} message={messages.createCampaign} />
          </Link>
        </When>
      </header>

      <section>
        <When predicate={campaigns.length > 0}>
          <CampaignsTable campaigns={campaigns} isAppointmentTypeVenue={isAppointmentTypeVenue} />
        </When>

        <When predicate={campaigns.length === 0}>
          <Card flush className={element('no-active-campaigns')}>
            <Isvg src={ICONS.priceTagBlue} />

            <When predicate={amount > 0}>
              <p className={element('revenue-paragraph')}>
                <Message
                  message={messages.madeFromPastCampaigns}
                  values={{
                    revenue: (
                      <Message
                        className={element('revenue-number')}
                        value={amount}
                        format="currency"
                        currency={currency}
                      />
                    ),
                  }}
                />
              </p>
            </When>

            <Message as="p" className={element('revenue-paragraph')} message={messages.noActiveCampaigns} />

            <When predicate={isAnyCampaignOptionAvailable}>
              <Link to={addUrl}>
                <Message
                  as={Button}
                  className={element('revenue-cta')}
                  styled="primary-outline"
                  message={messages.createCampaign}
                />
              </Link>
            </When>
          </Card>
        </When>
      </section>
    </section>
  );
}

function PastCampaigns() {
  const { inactiveCampaigns: campaigns } = useContext(CampaignsContext);
  const isAppointmentTypeVenue = useSelector(selectIsAppointmentTypeVenue);

  if (campaigns.length === 0) {
    return null;
  }

  return (
    <section className={element('section')}>
      <header className={element('header', { 'small-gap': true })}>
        <Isvg src={ICONS.history} />
        <Message as="h3" className={element('heading')} message={messages.pastCampaigns} />
      </header>
      <section>
        <CampaignsTable campaigns={campaigns} isAppointmentTypeVenue={isAppointmentTypeVenue} />
      </section>
    </section>
  );
}

const applyStrongAndBreak = {
  strong: chunks => <strong>{chunks}</strong>,
  br: () => <br />,
};

function HowItWorks() {
  const { allCampaigns: campaigns } = useContext(CampaignsContext);

  if (campaigns.length > 0) {
    return null;
  }

  return (
    <section className={element('section')}>
      <header className={element('header', { 'small-gap': true })}>
        <Message as="h3" className={element('heading')} message={messages.howItWorks} />
      </header>
      <section className={element('carousel')}>
        <HowItWorksCard1 />
        <HowItWorksCard2 />
        <HowItWorksCard3 />
      </section>
    </section>
  );
}

function Hero() {
  const dispatch = useDispatch();
  const { allCampaigns: campaigns, isAnyCampaignOptionAvailable } = useContext(CampaignsContext);
  const venue = useSelector(selectActiveVenue);
  const isAppointmentTypeVenue = useSelector(selectIsAppointmentTypeVenue);
  const addUrl = makePath.campaignsAdd({ venueId: venue.venueId });

  const isFoodAndBeverage = isVenueDashboardSubtypeFoodAndBeverage(venue);
  const isWellness = !isFoodAndBeverage && isAppointmentTypeVenue;

  const heroSrc = () => {
    if (isFoodAndBeverage) {
      return fnbHero;
    }

    if (isWellness) {
      return wellnessHero;
    }

    return fitnessHero;
  };

  function openLearnMoreModal() {
    dispatch(openModal('CampaignsModal', { className: 'slideshow-modal__modal-content' }));
  }

  return (
    <section className={element('hero')}>
      <Message as="h2" className={element('hero-heading')} message={messages.heroHeading} />

      <Message
        as="p"
        className={element('hero-paragraph')}
        message={messages.heroParagraph}
        values={applyStrongAndBreak}
      />

      <section className={element('hero-buttons')}>
        <Link to={addUrl}>
          <Button disabled={!isAnyCampaignOptionAvailable} className={element('hero-button')}>
            <Message message={messages.createCampaign} />

            <When predicate={!isAnyCampaignOptionAvailable}>
              <TooltipToggle name="create-campaign-disable-tooltip" className={element('hero-tooltip-toggle')}>
                <Isvg src={ICONS.infoGrey} className={element('hero-info-icon')} />
              </TooltipToggle>
              <TooltipContent
                name="create-campaign-disable-tooltip"
                className={element('hero-tooltip-content')}
                hoverable
                place="right">
                <Message message={messages.createCampaignDisabledTooltip} />
              </TooltipContent>
            </When>
          </Button>
        </Link>

        <When predicate={campaigns.length > 0}>
          <Message
            className={element('hero-button')}
            as={Button}
            styled="primary-outline"
            onClick={openLearnMoreModal}
            message={messages.learnMore}
          />
        </When>
      </section>

      <img src={heroSrc()} alt="" className={element('hero-image')} />
    </section>
  );
}

function CampaignsV2() {
  return (
    <CampaignsContextProvider>
      <Message message={messages.pageTitle} as={Title} />

      <section className={block()}>
        <Hero />

        <HowItWorks />

        <ActiveCampaigns />

        <PastCampaigns />
      </section>
    </CampaignsContextProvider>
  );
}

export default CampaignsV2;
